
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { LayoutDashboard, Wallet, Settings, Moon, ShoppingCart, Plus, ShoppingBag, Menu } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useCart } from '../../context/CartContext';
import { ViewState } from '../../types';
import { GhostBrandIcon } from '../ui/PartnerIcons';

interface MobileNavProps {
    currentView: ViewState;
    onNavigate: (view: ViewState) => void;
    onConnectWallet?: () => void;
    onOpenMobileMenu?: () => void;
}

const MobileNavItem = ({ view, icon: Icon, currentView, onNavigate }: { view: ViewState, icon: any, currentView: ViewState, onNavigate: (view: ViewState) => void }) => (
    <button
        onClick={() => onNavigate(view)}
        className={`flex flex-col items-center justify-center p-2 rounded-xl transition-all ${
            currentView === view ? 'text-[#0A0B0F] dark:text-white' : 'text-gray-400 hover:text-[#0A0B0F]'
        }`}
    >
        <Icon size={24} strokeWidth={currentView === view ? 2.5 : 2} />
    </button>
);

export const MobileNav: React.FC<MobileNavProps> = ({ currentView, onNavigate, onOpenMobileMenu }) => {
    const { toggleTheme } = useTheme();
    const { items } = useCart();
    
    return (
        <>
            {/* Mobile Header */}
            <header className="md:hidden sticky top-0 z-30 bg-[#0A0B0F] text-white px-4 py-4 flex justify-between items-center shadow-lg border-b border-[#1a1a1a]">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={onOpenMobileMenu}
                        className="p-1.5 rounded-lg text-stone-400 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Open Menu"
                    >
                        <Menu size={24} />
                    </button>
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center">
                            <GhostBrandIcon className="w-full h-full" />
                        </div>
                        <span className="font-serif text-lg tracking-wide"><span className="font-light">Ghost</span><span className="font-bold">Pay</span></span>
                    </div>
                </div>
                <div className="flex gap-4 text-gray-400">
                    <button onClick={toggleTheme}><Moon size={20}/></button>
                    <button onClick={() => onNavigate('cart')} className="relative">
                        <ShoppingCart size={20}/>
                        {items.length > 0 && <div className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></div>}
                    </button>
                </div>
            </header>

            {/* Mobile Bottom Nav */}
            <div className="md:hidden fixed bottom-6 left-4 right-4 z-50">
                <div className="bg-white/95 dark:bg-[#1a1a1a]/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-200 dark:border-[#2D3142] p-2 flex justify-around items-center h-16">
                    <MobileNavItem view="dashboard" icon={LayoutDashboard} currentView={currentView} onNavigate={onNavigate} />
                    <MobileNavItem view="wallet" icon={Wallet} currentView={currentView} onNavigate={onNavigate} />
                    
                    <button 
                        onClick={() => onNavigate('pos')}
                        className="w-12 h-12 bg-[#0A0B0F] dark:bg-white rounded-xl flex items-center justify-center text-white dark:text-[#0A0B0F] shadow-lg transform -translate-y-6 border-4 border-[#F8F9FA] dark:border-[#0A0B0F]"
                    >
                        <Plus size={24} />
                    </button>

                    <MobileNavItem view="marketplace" icon={ShoppingBag} currentView={currentView} onNavigate={onNavigate} />
                    <MobileNavItem view="settings" icon={Settings} currentView={currentView} onNavigate={onNavigate} />
                </div>
            </div>
        </>
    );
};
